import { Card, Col, Progress, Row, Typography } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons'
import { useI18n } from '../i18n/I18nProvider'

const { Text, Title } = Typography

export default function AppraisalWorkflowCard({
  title,
  total,
  completed,
  completedLabel,
  completionLabel,
  stats,
  statsOnly = false,
  secondaryValue,
  secondaryLabel,
  secondaryUrgent = false,
  showSecondaryStat = true,
  workflowStages
}) {
  const { t } = useI18n()
  const resolvedTitle = title || t('workflow.title', 'Appraisal Workflow')
  const resolvedCompletedLabel = completedLabel || t('common.completed', 'Completed')
  const completionPct = total ? Math.round((completed / total) * 100) : 0
  const secondaryColor = secondaryUrgent ? 'var(--tti-warning)' : 'var(--tti-info)'
  const displayedStats = stats || [
    {
      label: resolvedCompletedLabel,
      value: completed,
      color: 'var(--tti-success)',
      icon: <CheckCircleOutlined style={{ color: 'var(--tti-success)', fontSize: 18 }} />
    },
    showSecondaryStat && {
      label: secondaryLabel,
      value: secondaryValue ?? '—',
      color: undefined,
      icon: <ClockCircleOutlined style={{ fontSize: 18, color: secondaryColor }} />
    }
  ].filter(Boolean)

  if (statsOnly) {
    return (
      <Card size='small' style={{ marginBottom: 12 }}>

        <Title level={4}>{resolvedTitle}</Title>

        <Row gutter={[24, 16]} wrap={false}>
          {displayedStats.map(stat => (
            <Col key={stat.label} flex='1 1 0' style={{ minWidth: 0 }}>
              <div
                style={{
                  border: '1px solid var(--neutral-grey2)',
                  borderTop: `4px solid ${stat.color || 'var(--tti-info)'}`,
                  borderRadius: 6,
                  padding: '14px 16px',
                  height: '100%',
                  textAlign: 'center'
                }}
              >
                <div style={{ marginBottom: 4 }}>{stat.icon}</div>
                <Text type='secondary'>{stat.label}</Text>
                <div style={{ fontSize: 28, fontWeight: 700, color: stat.color, marginTop: 6 }}>{stat.value}</div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>
    )
  }

  return (
    <Card size='small' style={{ marginBottom: 12 }}>
      <Title level={4}>{resolvedTitle}</Title>
      <Row gutter={[24, 16]} align='middle' style={{ marginBottom: 20 }}>
        <Col xs={24} md={stats ? 12 : 16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <Text strong>{t('workflow.overall_completion', 'Overall completion')}</Text>
            <Text type='secondary'>{completionLabel || t('workflow.completed_count', '{{completed}} of {{total}} appraisals completed', { completed, total })}</Text>
          </div>
          <Progress percent={completionPct} strokeColor={completionPct === 100 ? 'var(--tti-success)' : 'var(--tti-info)'} />
        </Col>
        {displayedStats.map(stat => (
          <Col key={stat.label} xs={stats ? 8 : 12} md={4} style={{ textAlign: 'center' }}>
            {stat.icon}
            <div style={{ fontSize: 24, fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <Text type='secondary'>{stat.label}</Text>
          </Col>
        ))}
      </Row>

      <Row gutter={[10, 10]} wrap>
        {workflowStages.map((stage, index) => (
          <Col key={stage.label} flex='1 1 180px'>
            <div style={{ border: '1px solid var(--neutral-grey2)', borderTop: `4px solid ${stage.color}`, borderRadius: 6, padding: '12px 14px', height: '100%' }}>
              <Text type='secondary' style={{ fontSize: 12 }}>{t('workflow.step', 'STEP {{number}}', { number: index + 1 })}</Text>
              <div style={{ fontWeight: 600, color: stage.color }}>{stage.label}</div>
              <Text type='secondary' style={{ fontSize: 12 }}>{stage.detail}</Text>
              <div style={{ marginTop: 10 }}>
                <span style={{ fontSize: 24, fontWeight: 700 }}>{stage.count}</span>
                <Text type='secondary'> {t('workflow.waiting', 'waiting')}</Text>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    </Card>
  )
}
